"use client";
import React, { useEffect, useState } from "react";
import { useSearchParams } from "next/navigation";

import styles from "./SearchSolutions.module.scss";
import type { ImageProps } from "../shared/Image/interface";

// Helpers
import classnames from "classnames";
import search from "../../utils/search";
import DOMPurify from "@/app/[lang]/utils/dompurify";
import { useFeatureResizeCallback } from "../Features/useFeatureResizeCallback";

// Components
import { Flex } from "@/app/[lang]/components/ui/AntdLite";
import Button from "../ui/Button";
import Pagination from "../Pagination";
import Feature from "../Features/Feature";


const PAGE_SIZE = 9;

interface SearchSolutionsProps {
  title: string;
  buttonText: string;
  onClick?: () => void;
}

interface SolutionProps {
  id: string;
  heading: string;
  description: string;
  slug: string;
  searchImage?: { url: string };
  page_category?: { path: string };
}

export const SearchSolutions = ({
  title,
  buttonText,
  onClick,
}: SearchSolutionsProps) => {
  const searchParams = useSearchParams();
  const [currentPage, setCurrentPage] = useState(1);
  const [parsedResults, setParsedResults] = useState<any>({});
  const setRef = useFeatureResizeCallback(parsedResults.pages?.data ?? []);

  useEffect(() => {
    const query = searchParams.get("query");
    if (query) {
      search(query, "pages", currentPage, PAGE_SIZE)
        .then((res) => {
          return res.json();
        })
        .then((data) => {
          setParsedResults({
            total: data.pages?.meta?.pagination?.total || 0,
            ...data,
          });
        });
    }
  }, [searchParams, currentPage]);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  return (
    <section className={ classnames( 'bg-dark-blue text-white', styles.solutionsWrapper ) }>
      <div className="container">
        <h2 className={ styles.title } dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(title) }} />

        <Flex className={styles.solutions} gap="24px" wrap="wrap">
          {parsedResults.pages?.data?.map((solution: SolutionProps) => {
            const pageCategory = solution.page_category?.path;

            return (
              <Feature
                ref={setRef(solution.id)}
                key={solution.id}
                id={solution.id}
                showLink={false}
                newTab={true}
                title={solution.heading}
                description={solution.description}
                url={pageCategory ? `/${pageCategory}/${solution.slug}` : `/${solution.slug}`}
                media={{
                  data: { attributes: solution.searchImage } as ImageProps,
                }}
                text=""
              />
            )
          })}
        </Flex>

        {!onClick && parsedResults.total > PAGE_SIZE && (
          <Pagination
            defaultCurrent={currentPage}
            total={parsedResults.total}
            pageSize={PAGE_SIZE}
            onChange={handlePageChange}
            variant
          />
        )}

        {onClick && parsedResults.total > PAGE_SIZE && (
          <Flex justify="flex-end">
            <Button
              attributes={{
                type: "outline",
                backgroundColor: "dark-blue",
                textColor: "white",
                hoverColor: "light-blue",
              }}
              style={{ background: 'none' }}
              onClick={onClick}
            >
              {buttonText}
            </Button>
          </Flex>
        )}
      </div>
    </section>
  );
};
